import { useCallback, useEffect, useState } from 'react';

import isBefore from 'date-fns/isBefore';

import { warn } from '@adsk/alloy-react-helpers';

import { getRangeValue, isDateControlled, isPropControlled } from '../helpers';
import { DateRange } from '../types';

function useSelection({
  range,
  defaultDate,
  defaultStartDate,
  defaultEndDate,
  propsDate,
  propsStartDate,
  propsEndDate,
  propsOnChange,
  allowSameStartAndEndDate,
  onFinalSelection,
}: {
  defaultDate: Date | null;
  defaultStartDate: Date | null;
  defaultEndDate: Date | null;
  range?: boolean;
  propsDate?: Date | null;
  propsStartDate?: Date | null;
  propsEndDate?: Date | null;
  propsOnChange?: (selectedDates: Date | null | DateRange) => void;
  allowSameStartAndEndDate?: boolean;
  onFinalSelection: () => void;
}) {
  const [stateDate, setStateDate] = useState(defaultDate);
  const [stateStartDate, setStateStartDate] = useState(defaultStartDate);
  const [stateEndDate, setStateEndDate] = useState(defaultEndDate);

  const date = isPropControlled(propsDate) ? propsDate : stateDate;
  const startDate = isPropControlled(propsStartDate)
    ? propsStartDate
    : stateStartDate;
  const endDate = isPropControlled(propsEndDate) ? propsEndDate : stateEndDate;

  const isControlled = isDateControlled({
    range,
    date: propsDate,
    startDate: propsStartDate,
    endDate: propsEndDate,
  });

  useEffect(() => {
    if (
      range &&
      isControlled &&
      endDate &&
      startDate &&
      isBefore(endDate, startDate)
    ) {
      warn(
        'DatePicker: provided `endDate` is before provided `startDate` - this usage is not supported.'
      );
    }
  }, [range, isControlled, endDate, startDate]);

  const onDateSelected = useCallback(
    (
      {
        selectable,
        date: newlySelectedDate,
      }: { selectable?: boolean; date: Date },
      updateActiveMonths: (
        range: boolean,
        date?: Date | null,
        startDate?: Date,
        endDate?: Date
      ) => void
    ) => {
      if (!selectable) {
        return;
      } else if (isControlled) {
        if (range) {
          const selectedDates = getRangeValue({
            date: newlySelectedDate,
            startDate: propsStartDate,
            endDate: propsEndDate,
            allowSameStartAndEndDate,
          });

          propsOnChange?.(selectedDates);

          const [nextStartDate, nextEndDate] = selectedDates;

          if (nextStartDate && nextEndDate) {
            onFinalSelection();
            updateActiveMonths(range, null, nextStartDate, nextEndDate);
          }
        } else {
          propsOnChange?.(newlySelectedDate);
          updateActiveMonths(false, newlySelectedDate);
          onFinalSelection();
        }
      } else if (range) {
        const selectedDates = getRangeValue({
          date: newlySelectedDate,
          startDate: stateStartDate,
          endDate: stateEndDate,
          allowSameStartAndEndDate,
        });

        const [nextStartDate, nextEndDate] = selectedDates;

        setStateStartDate(nextStartDate);
        setStateEndDate(nextEndDate);

        if (nextStartDate && nextEndDate) {
          onFinalSelection();
          updateActiveMonths(range, null, nextStartDate, nextEndDate);
        }
      } else {
        setStateDate(newlySelectedDate);
        onFinalSelection();
        updateActiveMonths(false, newlySelectedDate);
      }
    },
    [
      allowSameStartAndEndDate,
      isControlled,
      propsEndDate,
      propsStartDate,
      stateEndDate,
      stateStartDate,
      range,
      propsOnChange,
      onFinalSelection,
    ]
  );

  const resetSelection = useCallback(() => {
    if (isControlled) {
      if (range) {
        propsOnChange?.([null, null]);
      } else {
        propsOnChange?.(null);
      }
    } else {
      if (range) {
        setStateStartDate(null);
        setStateEndDate(null);
      } else {
        setStateDate(null);
      }
    }
  }, [isControlled, range, propsOnChange]);

  return {
    date,
    startDate,
    endDate,
    onDateSelected,
    resetSelection,
  };
}

export default useSelection;
